/**
 * ForgotPasswordPage - Password reset request page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthLayout } from '../components/AuthLayout';
import { useAuthStore } from '@/stores';
import { cn } from '@/utils/cn';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { resetPassword, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const result = await resetPassword(email);
    if (!result.error) {
      setSubmitted(true);
    }
  };

  return (
    <AuthLayout
      title="Reset Password"
      subtitle="Enter your email to receive a password reset link"
    >
      {submitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-4"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Check Your Email</h3>
          <p className="text-text-secondary text-sm mb-6">
            If an account exists for <span className="text-white font-medium">{email}</span>,
            you'll receive a password reset link.
          </p>
          <Link
            to="/login"
            className={cn(
              'inline-block px-6 py-3 rounded-xl font-semibold',
              'bg-gradient-to-r from-hud-primary to-teal-600',
              'text-white',
              'hover:shadow-lg hover:shadow-hud-primary/25',
              'transition-all duration-200'
            )}
          >
            Back to Login
          </Link>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm"
            >
              {error}
            </motion.div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className={cn(
                'w-full px-4 py-3 rounded-xl',
                'bg-slate-800/50 border border-glass-border',
                'text-white placeholder-text-muted',
                'focus:outline-none focus:border-hud-primary focus:ring-1 focus:ring-hud-primary',
                'transition-all duration-200'
              )}
              placeholder="you@example.com"
            />
          </div>

          <motion.button
            type="submit"
            disabled={isLoading || !email}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className={cn(
              'w-full py-3 rounded-xl font-semibold',
              'bg-gradient-to-r from-hud-primary to-teal-600',
              'text-white',
              'hover:shadow-lg hover:shadow-hud-primary/25',
              'transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </motion.button>

          <p className="text-center text-sm text-text-secondary">
            Remember your password?{' '}
            <Link to="/login" className="text-hud-primary hover:text-hud-primary/80 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
